import { IKey } from '@/common/constants/ikey'
import { CoreExecutorType, getCoreType } from '@/common/constants/core'
import {
  ensureUniDataValueForJackson,
  setType,
  toClientExchangeUniDataDocumentField,
} from '@/common/utils/typedValueSerializer'
import { isProxy, toRaw } from 'vue'
import { createPlatformActionParams } from '@/common/utils/requestSanitizer'
import { normalizePlatformFlowContextCurrentData } from '@/common/utils/normalizePlatformFlowContextFields'
import { useAppStore } from '@/stores/app'
import apiService from '../api.service'
import { formHasRenderableLayout, normalizeFormOverlays, parseService } from '../parse/parse.service'
import type {
  ActionContext,
  ExecuteProcedureParams,
  FormData,
  FormPath
} from './types'
import { ActionError } from '@/common/exceptions'
import { localPropertiesService } from '@/services/local-properties/local-properties.service'
import {
  getDialogClosedScriptNode,
  getDialogClosedScriptText,
  getDialogClosedNReturnScriptNode,
  getDialogClosedNReturnScriptText,
} from '@/common/utils/dialogClosedScriptMeta'
import {
  resolveOnCloseDialogWebClientExec,
  shouldExecuteActionScriptOnClientFromActionItem,
} from '@/services/action/actionScriptRuntime'
import { resolveLayoutMergeKeysForGetData } from '@/common/utils/dialogCloseParentMergeScope'
import { resolveClientDataActualFields } from '@/common/utils/resolveClientDataActualFields'
import { applyListboxWireTypesFromListboxFields } from '@/common/utils/comboBoxSwingFields'
import {
  coerceExchangeTypedFieldForPayload,
  toTypedUniDataTableWireField,
} from '@/common/utils/jsonUniData'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { resolveFormSchemaIdFromPageEntry } from '@/common/utils/resolvePageIdentity'
import { normalizePlatformReloadElementResponse } from '@/common/utils/treeTableReloadResponseNormalize'
import { extractTablePaginationClientData } from '@/common/utils/tablePaginationReload'
import { assertPlatformActionResponse } from './platformActionResponse'
import { resolveElementActionCoreParams } from './resolveElementActionCoreParams'
import { isMainFrameOwnedSession } from '@/common/utils/mainFrameSlot'
import {
  noteRetiredRequestCore,
  rewriteMountedPlatformSession,
  resolveLivePlatformSessionId,
  shouldRetireActionRequestCore,
} from '@/services/action/retiredRequestCore'

export const FORM_PARSE_ATTEMPTED_FLAG = '$formParseAttempted'

function rememberRetiredPlatformAction(
  request: { sessionId?: unknown; isClone?: unknown },
  response: { sessionId?: unknown; status?: unknown } | null | undefined,
): void {
  if (
    !shouldRetireActionRequestCore({
      requestSessionId: request?.sessionId,
      responseSessionId: response?.sessionId,
      status: response?.status,
      clonedRequest: request?.isClone === true,
    })
  ) {
    return
  }
  noteRetiredRequestCore(request.sessionId, response?.sessionId)
  try {
    const appStore = useAppStore()
    rewriteMountedPlatformSession(
      [appStore.pagesData, appStore.dialogs, appStore.overlays],
      request.sessionId,
      response?.sessionId,
    )
  } catch {
    /* store is not ready */
  }
}

/** TreeTable aux tables are not a reload body — keep them out of preserve/drop. */
function isReloadUniDataTableField(fieldKey: string, value: any): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const fieldType = String((value as any).type ?? '')
  const fieldValue = (value as any).value
  const isTablePayload =
    fieldType === 'UniDataTable' ||
    fieldType === 'UniDataTableFields' ||
    Array.isArray((value as any).rows) ||
    Array.isArray(fieldValue?.rows)
  if (!isTablePayload) {
    return false
  }
  if (
    fieldKey.endsWith('_params') ||
    fieldKey.endsWith('_selected') ||
    fieldKey.endsWith('__selected')
  ) {
    return false
  }
  return true
}

/**
 * Поля POST /reloadElement: body перезагружаемой TABLE не эхоить;
 * sibling tables из En.getData / Identificator — typed UniDataTable (иначе
 * createSafeClientData их отбросит).
 */
export function normalizeReloadElementFormFields(
  form: Record<string, unknown> | null | undefined,
  preserveTableFields: Iterable<string> = [],
): Record<string, any> {
  const preserve = new Set<string>()
  for (const key of preserveTableFields) {
    if (key) preserve.add(key)
  }

  const normalizedFields: Record<string, any> = {}
  Object.entries(form || {}).forEach(([key, value]) => {
    if (value === undefined) {
      return
    }
    if (isReloadUniDataTableField(key, value)) {
      if (preserve.has(key)) {
        normalizedFields[key] = toTypedUniDataTableWireField(value) ?? value
      }
      return
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if ('type' in (value as any) && 'value' in (value as any)) {
        const typedValue = value as { type: string; value: any }
        if (typedValue.type === 'HashMap' || typedValue.type === 'Map') {
          return
        }
      }

      normalizedFields[key] = value
      return
    }

    if (value !== null && value !== undefined) {
      let fieldType: string
      if (typeof value === 'number') {
        fieldType = Number.isInteger(value) ? 'Integer' : 'Double'
      } else if (typeof value === 'boolean') {
        fieldType = 'Boolean'
      } else if (value instanceof Date) {
        fieldType = 'Date'
      } else {
        fieldType = 'String'
      }

      normalizedFields[key] = {
        type: fieldType,
        value: value instanceof Date ? value.toISOString() : value,
      }
    } else {
      normalizedFields[key] = {
        type: 'String',
        value: null,
      }
    }
  })
  return normalizedFields
}

type ReloadElementInflightEntry = {
  promise: Promise<any>
  payloadKey: string
}

const reloadElementInflight = new Map<string, ReloadElementInflightEntry>()

function stableReloadPayloadKey(value: any): string {
  if (value == null || typeof value !== 'object') return JSON.stringify(value ?? null)
  const seen = new WeakSet<object>()
  const normalize = (input: any): any => {
    if (input == null || typeof input !== 'object') return input
    if (seen.has(input)) return '[Circular]'
    seen.add(input)
    if (Array.isArray(input)) return input.map(normalize)
    return Object.keys(input).sort().reduce((acc: Record<string, any>, key) => {
      acc[key] = normalize(input[key])
      return acc
    }, {})
  }
  try {
    return JSON.stringify(normalize(value))
  } catch {
    return ''
  }
}

function reloadElementDedupeKey(payload: Record<string, any>): string {
  return [
    payload.sessionId ?? '',
    payload.type ?? '',
    payload.elementId ?? '',
  ].join('|')
}

export function createSafeClientData(data: any = {}) {
  const unwrap = <T>(value: T): T => (isProxy(value) ? toRaw(value) as T : value)
  const normalizeDocumentVersionValue = (fieldName: string, value: any) => {
    if (fieldName !== '_versionId') {
      return value
    }
    if (value === null || value === undefined) {
      return null
    }
    if (typeof value === 'string') {
      const trimmedValue = value.trim()
      return trimmedValue === '' || trimmedValue.toLowerCase() === 'null' ? null : trimmedValue
    }
    return value
  }
  const shouldOmitDocumentVersionField = (fieldName: string, value: any) => (
    fieldName === '_versionId' && (value === null || value === undefined)
  )
  const normalizeFieldValue = (fieldName: string, value: any) => {
    const normalizedValue = normalizeDocumentVersionValue(fieldName, value)
    return shouldOmitDocumentVersionField(fieldName, normalizedValue) ? undefined : normalizedValue
  }
  const getDefaultValueForFieldType = (type?: unknown) => {
    const normalizedType = typeof type === 'string' ? type : String(type ?? '')
    switch (normalizedType) {
      case 'UniData':
        return { fields: { fields: {} } }
      case 'UniDataTable':
      case 'UniDataTableFields':
        return { rows: [] }
      case 'Boolean':
        return false
      case 'Long':
      case 'Double':
      case 'BigDecimal':
      case 'Integer':
        return 0
      default:
        return ''
    }
  }
  /** wrapScriptValue / JSON-остаток `{__str}` → plain scalar для wire. */
  const unwrapWireScalar = (value: any) => {
    const fromScript = unwrapScriptValue(value)
    if (
      fromScript &&
      typeof fromScript === 'object' &&
      !Array.isArray(fromScript) &&
      Object.prototype.hasOwnProperty.call(fromScript, '__str') &&
      Object.keys(fromScript as object).length === 1 &&
      typeof (fromScript as { __str?: unknown }).__str === 'string'
    ) {
      return (fromScript as { __str: string }).__str
    }
    return fromScript
  }

  const unwrapFieldValue = (value: any) => {
    const unwrapped = unwrap(value)
    if (unwrapped && typeof unwrapped === 'object') {
      const rawValue = unwrapWireScalar(unwrap((unwrapped as any).value))
      if (rawValue !== undefined) {
        return { ...(unwrapped as any), value: rawValue }
      }
      if ('type' in (unwrapped as any)) {
        return {
          ...(unwrapped as any),
          value: getDefaultValueForFieldType((unwrapped as any).type),
        }
      }
    }
    return unwrapped
  }

  const actualFields = resolveClientDataActualFields(data)
  const fieldAttributes = data?.fields?.fieldAttributes || data?.fieldAttributes || {}
  const errors = data?.fields?.errors || data?.errors || {}
  const attributes = data?.fields?.attributes || data?.attributes || {}

  const sanitizedFields: Record<string, any> = {}
  for (const [key, value] of Object.entries(actualFields)) {
    const normalizedValue = unwrapFieldValue(value)
    if (value && typeof value === 'object' && 'type' in value) {
      if ('value' in normalizedValue) {
        let fieldValue = normalizeFieldValue(key, (normalizedValue as any).value)
        if (fieldValue === null || fieldValue === undefined) {
          fieldValue = getDefaultValueForFieldType((normalizedValue as any).type)
        }
        if (fieldValue === undefined) {
          continue
        }
        const normalizedField = fieldValue === (normalizedValue as any).value
          ? normalizedValue
          : { ...(normalizedValue as any), value: fieldValue }
        // Allow nested UniData payloads; иначе частичный clientData в value ломает UniClientDataDeserializer на сервере.
        if ((normalizedField as any).type === 'UniData') {
          const inner = (normalizedField as any).value
          sanitizedFields[key] = {
            ...(normalizedField as any),
            value:
              inner != null && typeof inner === 'object'
                ? ensureUniDataValueForJackson(inner)
                : inner,
          }
          continue
        }
        if (fieldValue === null ||
          fieldValue === undefined ||
          typeof fieldValue === 'string' ||
          typeof fieldValue === 'number' ||
          typeof fieldValue === 'boolean' ||
          (Array.isArray(fieldValue) && fieldValue.length === 0)) {
          sanitizedFields[key] = normalizedField
        } else if (typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
          const keys = Object.keys(fieldValue)
          if (keys.length <= 2 && (keys.includes('_text') || keys.includes('value'))) {
            sanitizedFields[key] = normalizedField
          } else if (
            (normalizedField as any).type === 'UniDataTable' ||
            (normalizedField as any).type === 'UniDataTableFields' ||
            Array.isArray((fieldValue as any).rows)
          ) {
            sanitizedFields[key] = normalizedField
          } else if (
            // Fallback: payload looks like UniData: { fields: { fields: ... } }
            (fieldValue as any)?.fields?.fields &&
            typeof (fieldValue as any).fields.fields === 'object' &&
            !Array.isArray((fieldValue as any).fields.fields)
          ) {
            sanitizedFields[key] = normalizedField
          }
        } else if (
          Array.isArray(fieldValue) &&
          (
            (normalizedField as any).type === 'UniDataTable' ||
            (normalizedField as any).type === 'UniDataTableFields' ||
            fieldValue.length > 0
          )
        ) {
          sanitizedFields[key] = normalizedField
        }
      }
    } else {
      const primitiveValue = normalizeFieldValue(key, normalizedValue)
      if (primitiveValue === undefined) {
        continue
      }
      if (primitiveValue === null ||
          primitiveValue === undefined ||
          typeof primitiveValue === 'string' ||
          typeof primitiveValue === 'number' ||
          typeof primitiveValue === 'boolean') {
        sanitizedFields[key] = setType(primitiveValue)
      } else if (
        typeof primitiveValue === 'object' &&
        !Array.isArray(primitiveValue) &&
        (key === 'source' || key === 'dest')
      ) {
        // Иначе объект не попадает в payload
        sanitizedFields[key] = toClientExchangeUniDataDocumentField(primitiveValue)
      }
    }
  }

  applyListboxWireTypesFromListboxFields(sanitizedFields)

  for (const [key, field] of Object.entries(sanitizedFields)) {
    const coerced = coerceExchangeTypedFieldForPayload(field)
    if (coerced) {
      sanitizedFields[key] = coerced
    }
  }

  const rawException = data?.exception
  const exception =
    rawException == null || typeof rawException === 'string'
      ? rawException ?? null
      : null

  const result = {
    fields: {
      fields: sanitizedFields,
      fieldAttributes: fieldAttributes,
      errors: errors,
      attributes: attributes
    },
    clientData: {
      elementConditions: data?.clientData?.elementConditions || null,
      reloadActions: data?.clientData?.reloadActions || null,
      params: data?.clientData?.params || {},
      doActionId: data?.clientData?.doActionId || 0,
      openPage: data?.clientData?.openPage !== undefined && data?.clientData?.openPage !== null ? data.clientData.openPage : 0
    },
    formError: data?.formError || null,
    exception
  }

  return result
}

function createSafeParams(params: any) {
  const { clientData: paramsClientData, ...paramsWithoutClientData } = params || {}

  const safeParams = {
    sessionId: null,
    isClone: false,
    type: getCoreType() || CoreExecutorType.WF,
    actionType: 1,
    actionId: '',
    clientData: createSafeClientData(paramsClientData),
    formState: {
      params: {},
      openPage: null,
      ...params?.formState
    },
    fids: params?.fids || [],
    currentFid: params?.currentFid || null,
    openingParams: {
      newWindow: false,
      ignoreExistingStack: false,
      tempFid: null,
      ...params?.openingParams
    },
    ...paramsWithoutClientData
  }

  try {
    const appStore = useAppStore()
    const ignoreStack = safeParams.openingParams?.ignoreExistingStack === true
    const storeFids = Array.isArray(appStore.formStackFids)
      ? appStore.formStackFids
      : []
    if (!ignoreStack && (!params?.fids || params.fids.length === 0) && storeFids.length) {
      safeParams.fids = [...storeFids]
    }

    const storeCurrentFid = typeof appStore.currentFormFid === 'string'
      ? appStore.currentFormFid
      : null
    if (!safeParams.currentFid && storeCurrentFid) {
      safeParams.currentFid = storeCurrentFid
    }
  } catch {
    // Unable to derive form stack from store
  }

  if (safeParams.clientData === null) {
    safeParams.clientData = createSafeClientData()
  }

  // Include saved element states (web analog of LocalProperties.ELEMENT_STATE) for server
  if (safeParams.clientData?.clientData) {
    safeParams.clientData.clientData[IKey.ELEMENT_STATES] = localPropertiesService.getElementStates()
  }

  // Middle `UserSession.getCoreFromRequest` не читает cloneOf — не кладём в /platform/action.
  delete (safeParams as { cloneOf?: unknown }).cloneOf

  return safeParams
}

function createExecuteProcedureParams(params: any) {
  const safeParams = {
    type: params?.type || getCoreType() || 'WF',
    sessionId: params?.sessionId || null,
    socketId: params?.socketId || null,
    isClone: params?.isClone !== undefined ? params?.isClone : false,
    currentData: createSafeClientData(params?.currentData),
    actionId: params?.actionId || '',
    cloneOf: params?.cloneOf || null,
  }

  if (safeParams.currentData === null) {
    safeParams.currentData = createSafeClientData()
  }

  normalizePlatformFlowContextCurrentData(safeParams.currentData)

  return safeParams
}

/** Парсинг XML/объекта формы из ответа platform/action (может блокировать main thread). */
export async function parsePlatformActionForm(
  documentData: any,
  actionId: unknown,
): Promise<void> {
  if (!documentData?.form) return

  const formIdNum = resolveFormSchemaIdFromPageEntry(
    documentData as Record<string, unknown>,
    actionId,
  )
  const rawForm = documentData.form
  if (typeof rawForm === 'string' && rawForm.length > 50_000) {
    await new Promise<void>((resolve) => setTimeout(resolve, 0))
  }
  const endParseMeasure = startWorkflowMeasure('parseService.getForm', {
    source: 'parsePlatformActionForm',
    formId: Number.isFinite(formIdNum) ? formIdNum : 0,
    isString: typeof rawForm === 'string',
  })
  const parsed = parseService.getForm(
    rawForm,
    Number.isFinite(formIdNum) ? formIdNum : 0,
  )
  endParseMeasure()
  documentData[FORM_PARSE_ATTEMPTED_FLAG] = true
  if (formHasRenderableLayout(parsed)) {
    documentData.form = parsed
  } else if (typeof rawForm !== 'string') {
    documentData.form = parsed
  }
  if (documentData.form && typeof documentData.form === 'object') {
    normalizeFormOverlays(
      documentData.form,
      Number.isFinite(formIdNum) ? formIdNum : 0,
    )
  }
}

export const coreService = {
  /**
   * Выполняет POST action элемента/формы на платформе.
   *
   * На вход получает `ActionContext`, собранный на уровне UI‑сервиса, и подготавливает
   * безопасные параметры для вызова платформенного API:
   *
   * - определяет фактическую платформу (`type`) по `ctx.platform`, `ctx.actionItem.platform`, `ctx.params.type` или `getCoreType()`;
   * - объединяет данные формы из `ctx.form` с полями `ctx.params.clientData.fields.fields` и формирует `clientData`;
   * TODO: исключить дублирование данных - form, pageData, clientData, params!
   * - `sessionId` / `isClone` по `window__type`: NEW_* клонирует core текущего окна (`sessionId` формы, не main);
   *   `cloneOf` middle не читает — в запрос не кладём.
   * - собирает итоговый объект параметров через `createSafeParams` и в зависимости от `window__type`
   *   вызывает `getOverlay`, `getWindowData` или `getDialog`.
   *
   * **Как используется `ctx.form`:**
   * - `ctx.form` трактуется как плоский объект полей формы (`fieldName -> value|{type,value}`);
   * TODO: ctx.form - должен быть подготовлен или нет? передавать сюда один подготовленный массив clientData!
   * - эти поля объединяются с полями из `ctx.params.clientData.fields.fields`;
   * - при конфликте ключей приоритет у `ctx.form` — т.е. актуальные значения из формы
   *   перекрывают значения, пришедшие в `clientData`;
   * - результирующий набор полей прокидывается в `clientData` и далее на мидл‑сервер.
   */
  async elementAction(ctx: ActionContext): Promise<any> {
    // Confirm показывается до вызова: ActionService.executeAction, legacy GlobalProvider.onAction,
    // invokeNestedElementActionFromClientScript — не дублировать window.confirm здесь.

    const actionPlatform =
      ctx.platform ||
      ctx.actionItem?.platform?._text ||
      ctx.params?.type ||
      getCoreType() ||
      CoreExecutorType.WF

    const rawParams = ctx?.params ? { ...(ctx as any).params } : {}
    const rawClientData = rawParams?.clientData ? { ...rawParams.clientData } : undefined

    if (rawParams) {
      delete rawParams.clientData
    }

    const resolvedRawFields = resolveClientDataActualFields(rawClientData || {})
    const rawFieldsContainer =
      Object.keys(resolvedRawFields).length > 0
        ? resolvedRawFields
        : (rawClientData?.fields?.fields ?? rawClientData?.fields ?? {})
    const mergedFields = {
      ...(ctx.form || {}),
      ...(rawFieldsContainer || {})
    }

    const combinedClientData = {
      ...(rawClientData || {}),
      fields: rawClientData?.fields && typeof rawClientData.fields === 'object'
        ? {
          ...(rawClientData.fields || {}),
          fields: mergedFields
        }
        : {
          fields: mergedFields
        }
    }


    const windowType = typeof ctx.actionItem.window__type?._text === 'string'
      ? ctx.actionItem.window__type._text.toUpperCase()
      : ''

    const hasExplicitSessionId = rawParams && Object.prototype.hasOwnProperty.call(rawParams, 'sessionId')
    const hasExplicitIsClone = rawParams && Object.prototype.hasOwnProperty.call(rawParams, 'isClone')
    const coreParams = resolveElementActionCoreParams({
      windowType,
      hasExplicitSessionId: Boolean(hasExplicitSessionId),
      explicitSessionId: rawParams?.sessionId,
      pageSessionId: ctx.pageData?.sessionId,
      hasExplicitIsClone: Boolean(hasExplicitIsClone),
      explicitIsClone: rawParams?.isClone,
    })
    // SAME_WINDOW doAction drops the request core after a successful open.
    // Home keeps MAIN_FRAME mounted, so that core must be cloned, not consumed.
    let forcedMainFrameClone = false
    const mainFrameSourceSession = coreParams.sessionId != null ? String(coreParams.sessionId) : ''
    if (!coreParams.isClone && mainFrameSourceSession) {
      try {
        if (isMainFrameOwnedSession(useAppStore().pagesData, mainFrameSourceSession)) {
          coreParams.isClone = true
          forcedMainFrameClone = true
        }
      } catch {
        /* store is not ready */
      }
    }

    if (rawParams) {
      delete rawParams.sessionId
      delete rawParams.isClone
      delete rawParams.cloneOf
    }

    const safeParams = createSafeParams({
      ...rawParams,
      actionId: Number(ctx.actionItem._attributes.id),
      sessionId: coreParams.sessionId,
      isClone: coreParams.isClone,
      clientData: combinedClientData,
      type: actionPlatform
    })

    if (ctx.actionItem.selected) {
      safeParams.clientData.fields = {
        ...ctx.actionItem.selected.fields,
        ...safeParams.clientData.fields,
      }
    }

    const dialogCloseCfg: Record<string, unknown> = {}
    const closeText = getDialogClosedScriptText(ctx.actionItem)
    if (closeText?.trim()) {
      dialogCloseCfg.onCloseDialogEvent = closeText.trim()
      const dcNode = getDialogClosedScriptNode(ctx.actionItem)
      dialogCloseCfg.onCloseDialogWebClientExec = resolveOnCloseDialogWebClientExec({
        actionItem: ctx.actionItem as Record<string, unknown>,
        scriptText: closeText.trim(),
        scriptNode: dcNode,
      })
    }
    const ownerActionId = Number(ctx.actionItem?._attributes?.id)
    if (Number.isFinite(ownerActionId) && ownerActionId > 0) {
      dialogCloseCfg.onCloseDialogActionElementId = ownerActionId
    }
    if (ctx.pageData?.sessionId != null && String(ctx.pageData.sessionId).trim() !== '') {
      dialogCloseCfg.onCloseDialogScriptSessionId = String(ctx.pageData.sessionId)
    }
    const parentSlotAtOpen =
      String((ctx as { pageDataSlot?: unknown }).pageDataSlot ?? '').trim() ||
      String((ctx as { parentPageDataSlot?: unknown }).parentPageDataSlot ?? '').trim()
    if (parentSlotAtOpen) {
      dialogCloseCfg.onCloseDialogParentSlotId = parentSlotAtOpen
    } else if (ctx.formId != null && String(ctx.formId).trim() !== '') {
      dialogCloseCfg.onCloseDialogParentSlotId = String(ctx.formId)
    }
    const nReturnText = getDialogClosedNReturnScriptText(ctx.actionItem)
    if (nReturnText?.trim()) {
      dialogCloseCfg.onCloseDialogNReturnEvent = nReturnText.trim()
      const nReturnNode = getDialogClosedNReturnScriptNode(ctx.actionItem)
      dialogCloseCfg.onCloseDialogNReturnWebClientExec = shouldExecuteActionScriptOnClientFromActionItem(
        ctx.actionItem,
        nReturnNode,
      )
    }

    const mergeScope = resolveLayoutMergeKeysForGetData(
      {
        data: ctx.actionItem,
        actionItem: ctx.actionItem,
        formId: ctx.formId,
        panelId: ctx.panelId,
        elementId:
          (ctx as any).elementId != null && String((ctx as any).elementId).trim() !== ''
            ? String((ctx as any).elementId)
            : undefined,
        pageId:
          (ctx as any).pageId != null && String((ctx as any).pageId).trim() !== ''
            ? String((ctx as any).pageId)
            : undefined,
        level: (ctx as any).level,
      },
      {
        getForm: ctx.controls?.getForm,
        getPageData: ctx.controls?.getPageData,
      },
    )
    if (!mergeScope.eventsEmptyMergeScope) {
      if (mergeScope.mergeFieldKeys !== undefined) {
        dialogCloseCfg.onCloseDialogParentMergeFieldKeys = [...mergeScope.mergeFieldKeys]
      }
      if (mergeScope.layoutLevel != null && String(mergeScope.layoutLevel).trim() !== '') {
        dialogCloseCfg.onCloseDialogParentMergeLayoutLevel = mergeScope.layoutLevel
      }
    }

    const config = dialogCloseCfg

    let result: any
    switch (windowType) {
      case 'NEW_WINDOW':
        result = await this.getOverlay({ ...ctx, params: safeParams, config })
        break
      case 'SAME_WINDOW':
      case 'MAIN_FRAME':
        result = await this.getWindowData(safeParams, ctx.pageData)
        break
      case 'NEW_DIALOG':
        result = await this.getDialog({ ...ctx, params: safeParams, config })
        break
      default:
        throw new Error(`Unknown window type: ${ctx.actionItem.window__type._text}`)
    }

    if (forcedMainFrameClone && result && Number(result.status) < 1) {
      const cloneId = String(result.sessionId ?? '').trim()
      if (cloneId && cloneId !== mainFrameSourceSession) {
        result.sessionId = mainFrameSourceSession
        void this.removeCore(cloneId)
      }
    }
    return result
  },

  // TODO: any -> ExchangeData!!!
  async executeProcedure(params: ExecuteProcedureParams): Promise<any> {
    const endMeasure = startWorkflowMeasure('coreService.executeProcedure', {
      actionId: params?.actionId,
      sessionId: params?.sessionId,
    })
    const safeParams = createExecuteProcedureParams(params)
    try {
      const response = await apiService.platformExecuteProcedure(safeParams)
      return response.data
    } finally {
      endMeasure()
    }
  },

  async reloadElement(path: FormPath, form: FormData = {}): Promise<any> {
    const endMeasure = startWorkflowMeasure('coreService.reloadElement', {
      formId: path?.formId,
      elementId: path?.elementId,
      sessionId: (path as any)?.sessionId,
    })
    if (!path || path.elementId === undefined || path.elementId === null) {
      throw new Error('reloadElement requires a valid elementId in path')
    }

    const elementId = Number(path.elementId)
    if (Number.isNaN(elementId)) {
      throw new Error(`reloadElement expected numeric elementId, received: ${path.elementId}`)
    }

    const normalizedFields = normalizeReloadElementFormFields(
      form as Record<string, unknown>,
      path.preserveTableFields ?? [],
    )

    const paginationClientData =
      path.fieldName && extractTablePaginationClientData(normalizedFields, path.fieldName)

    const clientData = createSafeClientData({
      fields: {
        fields: normalizedFields,
        fieldAttributes: {},
        errors: {},
        attributes: {}
      },
      ...(paginationClientData
        ? {
            clientData: paginationClientData,
          }
        : {}),
    })

    const payload: Record<string, any> = {
      sessionId: resolveLivePlatformSessionId((path as any).sessionId),
      elementId,
      clientData
    }

    const pathType = (path as any).type
    if (pathType && typeof pathType === 'string') {
      payload.type = pathType
    } else {
      const coreType = getCoreType()
      if (coreType && coreType !== CoreExecutorType.UNKNOWN) {
        payload.type = coreType
      }
    }

    const dedupeKey = reloadElementDedupeKey(payload)
    const payloadKey = stableReloadPayloadKey(payload)

    const inflight = reloadElementInflight.get(dedupeKey)
    if (inflight && inflight.payloadKey === payloadKey) {
      try {
        startWorkflowMeasure('coreService.reloadElement.dedupeHit', {
          mode: 'inflight',
          elementId,
          sessionId: payload.sessionId,
        })()
        return await inflight.promise
      } finally {
        endMeasure()
      }
    }

    try {
      const requestPromise = apiService.platformReloadElement(payload)
        .then((response) => {
          const data = response.data
          normalizePlatformReloadElementResponse(data)
          return data
        })
        .finally(() => {
          reloadElementInflight.delete(dedupeKey)
        })

      reloadElementInflight.set(dedupeKey, { promise: requestPromise, payloadKey })
      return await requestPromise
    } catch (error: any) {
      console.error('Failed to reload element:', path.elementId, error)
      throw error
    } finally {
      endMeasure()
    }
  },

  /**
   * Запрос списка значений для автодополнения (справочник suggester).
   * Использует тот же endpoint reload/element с method=getAutoCompleteList.
   * @param path - путь к элементу (formId, elementId, sessionId, type)
   * @param elementName - имя элемента (name из конфига)
   * @param formData - текущие данные формы (fields)
   * @param options - filter (текст в поле), cursor (позиция), forced
   * @returns массив вариантов (таблица __alternatives) или пустой массив
   */
  async getAutoCompleteList(
    path: FormPath,
    elementName: string,
    formData: FormData = {},
    options: { filter?: string; cursor?: number; forced?: boolean } = {}
  ): Promise<any[]> {
    if (!path || path.elementId === undefined || path.elementId === null) {
      throw new Error('getAutoCompleteList requires a valid elementId in path')
    }

    const elementId = Number(path.elementId)
    if (Number.isNaN(elementId)) {
      throw new Error(`getAutoCompleteList expected numeric elementId, received: ${path.elementId}`)
    }

    const filter = options.filter ?? ''
    const cursor = options.cursor ?? 0
    const forced = options.forced ?? false

    const autocompleteFields: Record<string, any> = {
      ...formData,
      [IKey.METHOD]: IKey.GET_AUTO_COMPLETE_LIST,
      [`${elementName}__${IKey.AUTO_COMPLETE_FILTER}`]: filter,
      [`${elementName}__${IKey.AUTO_COMPLETE_CURSOR}`]: cursor,
      [`${elementName}__${IKey.AUTO_COMPLETE_FORCED}`]: forced
    }

    const normalizedFields: Record<string, any> = {}
    for (const [key, value] of Object.entries(autocompleteFields)) {
      if (value === undefined) continue

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if ('type' in (value as any) && 'value' in (value as any)) {
          const typedValue = value as { type: string; value: any }
          if (typedValue.type === 'HashMap' || typedValue.type === 'Map') continue
        }
        normalizedFields[key] = value
        continue
      }

      if (value !== null && value !== undefined) {
        let fieldType: string
        if (typeof value === 'number') {
          fieldType = Number.isInteger(value) ? 'Integer' : 'Double'
        } else if (typeof value === 'boolean') {
          fieldType = 'Boolean'
        } else {
          fieldType = 'String'
        }
        normalizedFields[key] = {
          type: fieldType,
          value: value
        }
      } else {
        normalizedFields[key] = { type: 'String', value: null }
      }
    }

    const currentData = createSafeClientData({
      fields: {
        fields: normalizedFields,
        fieldAttributes: {},
        errors: {},
        attributes: {}
      }
    })

    const payload: Record<string, any> = {
      sessionId: resolveLivePlatformSessionId((path as any).sessionId),
      elementId,
      currentData
    }

    const pathType = (path as any).type
    if (pathType && typeof pathType === 'string') {
      payload.type = pathType
    } else {
      const coreType = getCoreType()
      if (coreType && coreType !== CoreExecutorType.UNKNOWN) {
        payload.type = coreType
      }
    }

    try {
      const response = await apiService.platformReloadElement(payload)
      const data = response.data
      normalizePlatformReloadElementResponse(data)
      const alternativesKey = elementName + IKey.ALTERNATIVES_SUFFIX
      const raw = data?.fields?.fields?.[alternativesKey]

      if (raw == null) return []

      if (Array.isArray(raw)) return raw

      // Таблица с бэкенда: { type: "UniDataTable", value: { columns, rows } } — строки в value.rows
      const value = (raw as any)?.value
      if (value != null && typeof value === 'object') {
        if (Array.isArray(value.rows)) return value.rows
        if (Array.isArray(value)) return value
      }
      if (Array.isArray((raw as any).value)) return (raw as any).value
      if (Array.isArray((raw as any).rows)) return (raw as any).rows
      return []
    } catch (error: any) {
      console.error('Failed to get autoComplete list for element:', elementName, error)
      throw error
    }
  },

  // TODO: описание, возможно заменить название
  async action(
    params: any,
    options?: { deferFormParse?: boolean },
  ): Promise<any> {
    const endMeasure = startWorkflowMeasure('coreService.action', {
      actionId: params?.actionId,
      actionType: params?.actionType,
      sessionId: params?.sessionId,
    })
    const safeParams = createPlatformActionParams(params)

    try {
      const response = await apiService.platformAction(safeParams)
      const documentData = response.data
      const id = params.actionId

      // Record the replacement before assert: a menu refetch can drop the
      // request core and still come back incomplete. Later STACK must not reuse it.
      rememberRetiredPlatformAction(safeParams, documentData)

      assertPlatformActionResponse(documentData, {
        actionId: safeParams.actionId ?? id,
        actionType: safeParams.actionType ?? params.actionType,
      })

      if (documentData?.form && !options?.deferFormParse) {
        await parsePlatformActionForm(documentData, id)
      }

      documentData.date = new Date()

      return documentData
    } catch (error: any) {
      if (!(error instanceof ActionError)) {
        console.error('Core action error:', error)
      }
      throw error
    } finally {
      endMeasure()
    }
  },

  async getWindowData(params: any, parent?: any): Promise<any> {
    return await this.action(params)
  },

  async getOverlay(context: any): Promise<any> {
    return await this.getWindowData(context.params, context.parent)
  },

  async getDialog(context: any): Promise<any> {
    const result = await this.getWindowData(context.params, context.parent)
    const cfg = context?.config
    if (cfg && typeof cfg === 'object' && Object.keys(cfg).length > 0) {
      // elementAction кладёт сюда dialogCloseCfg (скрипты закрытия)
      return { ...result, clientDialogCloseConfig: cfg }
    }
    return result
  },

  async removeCore(
    sessionId: string,
    options: { keepalive?: boolean; skipStartupAck?: boolean } = {},
  ): Promise<void> {
    if (!sessionId || String(sessionId).trim() === '' || String(sessionId) === '-1') {
      return
    }

    const payload: { sessionId: string; skipStartupAck?: boolean } = { sessionId }
    if (options.skipStartupAck) {
      payload.skipStartupAck = true
    }

    if (options.keepalive && typeof fetch === 'function') {
      try {
        const apiBaseUrl = import.meta.env.DEV
          ? '/api'
          : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:1080/workflow-web-services/api')
        const csrfToken = document.cookie
          .split(';')
          .map((item) => item.trim())
          .find((item) => item.startsWith('CSRF-TOKEN='))
          ?.split('=')[1]
        await fetch(`${apiBaseUrl}/platform/remove`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfToken ? { 'X-CSRF-TOKEN': decodeURIComponent(csrfToken) } : {}),
          },
          body: JSON.stringify(payload),
          credentials: 'include',
          keepalive: true,
        })
        return
      } catch {
        // Fall back to normal axios request below.
      }
    }

    await apiService.platformRemove(payload)
  },

  async removeDialog(id: string, sessionId: string): Promise<void> {
    await this.removeCore(sessionId)
  },


}

export default coreService
